import React, { useState, useRef, useEffect } from 'react';
import { Badge, Typography, Tooltip, Input } from 'antd';
import {
  EyeOutlined,
  UserOutlined,
  BankOutlined,
  FileOutlined,
  DeleteOutlined,
  CloseOutlined,
  ClearOutlined,
  EditOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { useWatchlist, WatchlistItem } from './WatchlistContext';
import { dateTimeFormat } from '../../common';

const { Text } = Typography;

interface WatchlistProps {
  history: {
    push: (path: string) => void;
  };
}

// Add CSS styles for the watchlist
if (typeof document !== 'undefined') {
  const styleId = 'watchlist-floating-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = `
      .watchlist-tab {
        position: fixed;
        bottom: 20px;
        right: 20px;
        background: linear-gradient(135deg, #16a34a 0%, #22c55e 100%);
        color: white;
        border: none;
        border-radius: 12px 12px 4px 12px;
        padding: 12px 16px;
        cursor: pointer;
        font-size: 13px;
        font-weight: 600;
        box-shadow: 
          0 8px 25px rgba(22, 163, 74, 0.3),
          0 4px 12px rgba(0, 0, 0, 0.15);
        z-index: 1040;
        transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        display: flex;
        align-items: center;
        gap: 8px;
        backdrop-filter: blur(10px);
        min-width: 120px;
        animation: watchlistTabEntrance 0.5s cubic-bezier(0.16, 1, 0.3, 1);
      }
      
      @keyframes watchlistTabEntrance {
        0% {
          opacity: 0;
          transform: translateY(40px) translateX(20px) scale(0.8);
        }
        100% {
          opacity: 1;
          transform: translateY(0) translateX(0) scale(1);
        }
      }
      
      .watchlist-tab:hover {
        transform: translateY(-2px);
        box-shadow: 
          0 8px 25px rgba(22, 163, 74, 0.35),
          0 4px 12px rgba(0, 0, 0, 0.15);
        background: linear-gradient(135deg, #15803d 0%, #16a34a 100%);
      }
      
      .watchlist-tab:active {
        transform: translateY(-1px);
      }
      
      .watchlist-panel {
        position: fixed;
        bottom: 90px;
        right: 20px;
        width: 450px;
        max-width: 90vw;
        max-height: 500px;
        background: rgba(255, 255, 255, 0.98);
        border-radius: 16px;
        box-shadow: 
          0 20px 40px rgba(0, 0, 0, 0.12),
          0 8px 16px rgba(0, 0, 0, 0.08);
        border: 1px solid rgba(0, 0, 0, 0.08);
        z-index: 1041;
        display: flex;
        flex-direction: column;
        backdrop-filter: blur(12px);
        animation: watchlistPanelEntrance 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        overflow: hidden;
        transform-origin: bottom right;
      }
      
      @keyframes watchlistPanelEntrance {
        0% {
          opacity: 0;
          transform: translateY(20px) scale(0.95);
        }
        100% {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }
      
      .watchlist-header {
        padding: 16px 20px 12px 20px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.06);
        background: rgba(255, 255, 255, 0.95);
        display: flex;
        align-items: center;
        justify-content: space-between;
        border-radius: 16px 16px 0 0;
        backdrop-filter: blur(8px);
      }
      
      .watchlist-content {
        flex: 1;
        overflow-y: auto;
        max-height: 400px;
        scrollbar-width: thin;
        scrollbar-color: rgba(22, 163, 74, 0.2) transparent;
      }
      
      .watchlist-content::-webkit-scrollbar {
        width: 6px;
      }
      
      .watchlist-content::-webkit-scrollbar-track {
        background: transparent;
      }
      
      .watchlist-content::-webkit-scrollbar-thumb {
        background: rgba(22, 163, 74, 0.2);
        border-radius: 3px;
      }
      
      .watchlist-content::-webkit-scrollbar-thumb:hover {
        background: rgba(22, 163, 74, 0.3);
      }
      
      .watchlist-item {
        padding: 14px 20px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.04);
        cursor: pointer;
        transition: all 0.15s ease;
        display: flex;
        align-items: flex-start;
        gap: 12px;
        position: relative;
        overflow: hidden;
        opacity: 0;
        animation: watchlistItemSlideIn 0.2s ease forwards;
        min-height: 60px;
      }
      
      .watchlist-item:nth-child(1) { animation-delay: 0.02s; }
      .watchlist-item:nth-child(2) { animation-delay: 0.04s; }
      .watchlist-item:nth-child(3) { animation-delay: 0.06s; }
      .watchlist-item:nth-child(4) { animation-delay: 0.08s; }
      .watchlist-item:nth-child(5) { animation-delay: 0.1s; }
      .watchlist-item:nth-child(n+6) { animation-delay: 0.12s; }
      
      @keyframes watchlistItemSlideIn {
        0% {
          opacity: 0;
          transform: translateX(10px);
        }
        100% {
          opacity: 1;
          transform: translateX(0);
        }
      }
      
      .watchlist-item::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        width: 0;
        height: 100%;
        background: linear-gradient(90deg, 
          rgba(22, 163, 74, 0.08) 0%, 
          rgba(22, 163, 74, 0.04) 100%);
        transition: width 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        z-index: -1;
      }
      
      .watchlist-item:hover {
        background: rgba(248, 250, 252, 0.9);
        transform: translateX(-2px);
        border-left: 3px solid rgba(22, 163, 74, 0.4);
        padding-left: 17px;
      }
      
      .watchlist-item:hover::before {
        width: 100%;
      }
      
      .watchlist-item:active {
        transform: translateX(-1px);
      }
      
      .watchlist-item-icon {
        width: 36px;
        height: 36px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        flex-shrink: 0;
        margin-top: 2px;
      }
      
      .watchlist-item-icon.employee {
        background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%);
        color: #3b82f6;
      }
      
      .watchlist-item-icon.employer {
        background: linear-gradient(135deg, rgba(168, 85, 247, 0.1) 0%, rgba(168, 85, 247, 0.05) 100%);
        color: #a855f7;
      }
      
      .watchlist-item-icon.ptb {
        background: linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(34, 197, 94, 0.05) 100%);
        color: #22c55e;
      }
      
      .watchlist-item-content {
        flex: 1;
        min-width: 0;
      }
      
      .watchlist-item-title {
        font-weight: 600;
        font-size: 14px;
        color: #1f2937;
        margin-bottom: 2px;
        word-wrap: break-word;
        line-height: 1.3;
      }
      
      .watchlist-item-subtitle {
        font-size: 12px;
        color: #6b7280;
        word-wrap: break-word;
        line-height: 1.4;
        margin-bottom: 2px;
      }
      
      .watchlist-item-actions {
        display: flex;
        align-items: center;
        gap: 4px;
        opacity: 0;
        transition: opacity 0.2s ease;
        flex-shrink: 0;
      }
      
      .watchlist-item:hover .watchlist-item-actions {
        opacity: 1;
      }
      
      .watchlist-action-button {
        width: 28px;
        height: 28px;
        border: none;
        background: rgba(0, 0, 0, 0.04);
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.2s ease;
        font-size: 12px;
      }
      
      .watchlist-action-button:hover {
        background: rgba(239, 68, 68, 0.1);
        color: #ef4444;
        transform: scale(1.1);
      }
      
      .watchlist-action-button.edit-button:hover {
        background: rgba(22, 163, 74, 0.1);
        color: #16a34a;
      }
      
      .watchlist-action-button.save-button:hover {
        background: rgba(59, 130, 246, 0.1);
        color: #3b82f6;
      }
      
      .watchlist-empty {
        padding: 40px 20px;
        text-align: center;
        color: #9ca3af;
      }
      
      .watchlist-empty-icon {
        font-size: 32px;
        margin-bottom: 12px;
        opacity: 0.6;
      }
      
      .watchlist-footer {
        padding: 12px 20px;
        border-top: 1px solid rgba(0, 0, 0, 0.06);
        background: rgba(249, 250, 251, 0.8);
        border-radius: 0 0 16px 16px;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      
      .watchlist-clear-button {
        background: rgba(107, 114, 128, 0.08);
        border: 1px solid rgba(107, 114, 128, 0.15);
        color: #6b7280;
        border-radius: 6px;
        padding: 6px 12px;
        font-size: 11px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        gap: 4px;
      }
      
      .watchlist-clear-button:hover {
        background: rgba(107, 114, 128, 0.12);
        border-color: rgba(107, 114, 128, 0.25);
        color: #4b5563;
        transform: translateY(-1px);
      }
      
      .watchlist-backdrop {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.02);
        z-index: 1040;
        animation: backdropFadeIn 0.2s ease;
      }
      
      @keyframes backdropFadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      
      .watchlist-note {
        margin-top: 6px;
        padding: 6px 8px;
        background: rgba(249, 250, 251, 0.8);
        border-radius: 6px;
        font-size: 11px;
        line-height: 1.4;
        color: #6b7280;
        border-left: 3px solid rgba(22, 163, 74, 0.3);
      }
      
      .watchlist-note-edit {
        margin-top: 6px;
      }
      
      .watchlist-note-edit .ant-input {
        font-size: 11px;
        border-radius: 4px;
        border: 1px solid rgba(22, 163, 74, 0.3);
        padding: 4px 6px;
        min-height: auto;
      }
      
      .watchlist-note-edit .ant-input:focus {
        border-color: #16a34a;
        box-shadow: 0 0 0 2px rgba(22, 163, 74, 0.1);
      }
      
      .watchlist-add-note-button {
        margin-top: 4px;
        background: none;
        border: 1px dashed rgba(22, 163, 74, 0.3);
        color: #16a34a;
        border-radius: 4px;
        padding: 4px 8px;
        font-size: 10px;
        cursor: pointer;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        gap: 4px;
        width: 100%;
        justify-content: center;
      }
      
      .watchlist-add-note-button:hover {
        background: rgba(22, 163, 74, 0.05);
        border-color: rgba(22, 163, 74, 0.4);
      }
    `;
    document.head.appendChild(style);
  }
}

const Watchlist: React.FC<WatchlistProps> = ({ history }) => {
  const { items, removeItem, clearWatchlist, updateNote } = useWatchlist();
  const [isOpen, setIsOpen] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteValue, setEditingNoteValue] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [isOpen]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [isOpen]);

  const getItemIcon = (type: WatchlistItem['type']) => {
    switch (type) {
      case 'employee':
        return <UserOutlined />;
      case 'employer':
        return <BankOutlined />;
      case 'ptb':
        return <FileOutlined />;
      default:
        return <EyeOutlined />;
    }
  };

  const handleItemClick = (item: WatchlistItem) => {
    history.push(item.route);
    setIsOpen(false);
  };

  const handleRemoveItem = (e: React.MouseEvent, itemId: string) => {
    e.stopPropagation();
    removeItem(itemId);
  };

  const handleClearAll = () => {
    clearWatchlist();
    setIsOpen(false);
  };

  const handleEditNote = (item: WatchlistItem) => {
    setEditingNoteId(item.id);
    setEditingNoteValue(item.note || '');
  };

  const handleSaveNote = (itemId: string) => {
    updateNote(itemId, editingNoteValue);
    setEditingNoteId(null);
    setEditingNoteValue('');
  };

  // Don't render if no items
  if (items.length === 0) {
    return null;
  }

  return (
    <>
      {/* Backdrop when panel is open */}
      {isOpen && <div className="watchlist-backdrop" />}

      {/* Watchlist Tab */}
      <Tooltip title="View watchlist" placement="left">
        <button
          type="button"
          className="watchlist-tab"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle watchlist"
        >
          <EyeOutlined />
          <span>Watchlist</span>
          <Badge
            count={items.length}
            size="small"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', color: '#16a34a' }}
          />
        </button>
      </Tooltip>

      {/* Watchlist Panel */}
      {isOpen && (
        <div ref={panelRef} className="watchlist-panel">
          {/* Header */}
          <div className="watchlist-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <EyeOutlined style={{ color: '#16a34a', fontSize: '16px' }} />
              <span style={{ fontWeight: 600, color: '#1f2937' }}>Watchlist</span>
              <Badge count={items.length} size="small" style={{ backgroundColor: '#16a34a' }} />
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#6b7280',
                fontSize: '16px',
                padding: '4px',
                borderRadius: '4px',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ef4444';
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#6b7280';
                e.currentTarget.style.background = 'none';
              }}
            >
              <CloseOutlined />
            </button>
          </div>

          {/* Content */}
          <div className="watchlist-content">
            {items.length === 0 ? (
              <div className="watchlist-empty">
                <EyeOutlined className="watchlist-empty-icon" />
                <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>No items in watchlist</div>
                <div style={{ fontSize: '12px', lineHeight: 1.4 }}>
                  Add employees, employers, or PTBs to monitor them easily
                </div>
              </div>
            ) : (
              items.map((item) => (
                <div
                  key={item.id}
                  className="watchlist-item"
                  onClick={() => handleItemClick(item)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handleItemClick(item)}
                >
                  <div className={`watchlist-item-icon ${item.type}`}>{getItemIcon(item.type)}</div>
                  <div className="watchlist-item-content">
                    <div className="watchlist-item-title">{item.title}</div>
                    {item.subtitle && <div className="watchlist-item-subtitle">{item.subtitle}</div>}
                    <div className="watchlist-item-subtitle">
                      Added {dateTimeFormat(item.timestamp, 'MMM DD, HH:mm')}
                    </div>

                    {/* Note display/edit */}
                    {editingNoteId === item.id && (
                      <div className="watchlist-note-edit">
                        <Input
                          value={editingNoteValue}
                          onChange={(e) => setEditingNoteValue(e.target.value)}
                          placeholder="Add a note..."
                          onPressEnter={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleSaveNote(item.id);
                          }}
                          onBlur={() => handleSaveNote(item.id)}
                          autoFocus
                          size="small"
                        />
                      </div>
                    )}
                    {editingNoteId !== item.id && item.note && (
                      <div className="watchlist-note">
                        <FileTextOutlined style={{ marginRight: 4, fontSize: '10px' }} />
                        {item.note}
                      </div>
                    )}
                    {editingNoteId !== item.id && !item.note && (
                      <button
                        type="button"
                        className="watchlist-add-note-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditNote(item);
                        }}
                      >
                        <EditOutlined />
                        Add note
                      </button>
                    )}
                  </div>
                  <div className="watchlist-item-actions">
                    {item.note && editingNoteId !== item.id && (
                      <button
                        type="button"
                        className="watchlist-action-button edit-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditNote(item);
                        }}
                        title="Edit note"
                      >
                        <EditOutlined />
                      </button>
                    )}
                    <button
                      type="button"
                      className="watchlist-action-button"
                      onClick={(e) => handleRemoveItem(e, item.id)}
                      title="Remove from watchlist"
                    >
                      <DeleteOutlined />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {items.length > 0 && (
            <div className="watchlist-footer">
              <Text type="secondary" style={{ fontSize: '11px' }}>
                {items.length} item{items.length !== 1 ? 's' : ''} • Click to navigate
              </Text>
              <button type="button" className="watchlist-clear-button" onClick={handleClearAll} title="Clear all items">
                <ClearOutlined />
                Clear All
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default Watchlist;
